import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { MstItemDetailWheelDetail } from "features/mst_wheel/types";
import { EstQuoteDetailTire, ITireItemDetail } from "features/car_model/types";
import { TireResponse } from "features/car_model/api/carModelApi";
import { TOption } from "type";
import { uniqBy } from "lodash";
import { PunctureProtection } from "features/mst_option/types/option";
import { getPructureProtectionApi } from "features/mst_option/api/optionApi";
import routes from "constants/routes";
import { SESSION_KEY } from "constants/key";
import { FastenerState } from "features/mst_store_tire_wheel_filter/types/wheel";
import { getItemDetailTireSetApi } from "features/mst_proc_fee/api/procFeeApi";
import {
  CategoryListItem,
  ITireInfo,
  MstItemDetailTireSelect,
  SelectedTireProcFeeParams,
  WheelFeeOptions,
} from "features/mst_proc_fee/types/proc-fee";
import fetchAndMapInventory from "features/mst_proc_fee/utils/inventoryUtils";
import {
  buildProcFeeFilterQueryParams,
  useProcFeeFilter,
  type ProcFeeFilterSelection,
} from "features/mst_proc_fee/hooks/useProcFeeFilter";
import { formatNumber } from "util/helper";

const PER_PAGE_DEFAULT = 20;

const useSessionData = () =>
  useMemo(() => {
    const tireCategory = sessionStorage.getItem(SESSION_KEY.TIRE_TYPE);
    const selectedCar = JSON.parse(sessionStorage.getItem(SESSION_KEY.NEW_QUOTE_MAKER) || "{}");
    const selectedWheels: {
      cost_setting: FastenerState;
      wheels_detail: MstItemDetailWheelDetail[];
    } = JSON.parse(sessionStorage.getItem(SESSION_KEY.SELECTED_WHEEL) || "{}");
    const tireSize: ITireInfo[] = JSON.parse(
      sessionStorage.getItem(SESSION_KEY.NEW_QUOTE_WHEEL) || "[]",
    );
    const storageSelectedMaker: TOption[] =
      JSON.parse(sessionStorage.getItem(SESSION_KEY.WHEEL_SELECTED_MAKER) || "[]") ?? [];
    const storageSelectedCategory: CategoryListItem[] =
      JSON.parse(sessionStorage.getItem(SESSION_KEY.WHEEL_SELECTED_CATEGORY) || "[]") ?? [];
    const currentPage = sessionStorage.getItem(SESSION_KEY.CURRENT_PAGE) || 1;
    const newQuoteFrom = sessionStorage.getItem(SESSION_KEY.NEW_QUOTE_FROM) || "";
    const newQuoteWheel = JSON.parse(sessionStorage.getItem(SESSION_KEY.NEW_QUOTE_WHEEL) || "[]");
    const newQuoteTire = JSON.parse(sessionStorage.getItem(SESSION_KEY.NEW_QUOTE_TIRE) || "[]");

    return {
      tireCategory,
      selectedCar,
      tireSize: tireSize[0],
      selectedWheel: selectedWheels.wheels_detail[0],
      storageTire: tireSize,
      storageSelectedMaker,
      storageSelectedCategory,
      currentPage,
      newQuoteFrom,
      newQuoteWheel,
      newQuoteTire,
    };
  }, []);

/**
 * Custom hook to fetch and manage a paginated list of procedure fees.
 */
const useWheelTireProcFeeList = () => {
  // Initialize costSettingInfo from session storage if available
  const [costSettingInfo, setCostSettingInfo] = useState<WheelFeeOptions>(() => {
    const mstProcFee = JSON.parse(sessionStorage.getItem(SESSION_KEY.MST_PROC_WHEEL_FEE) || "{}");

    if (mstProcFee?.proc_fees) {
      return {
        replac_labor_cost: formatNumber(mstProcFee.proc_fees.replac_labor_cost?.value) || "0",
        waste_tire_cost_flag: mstProcFee.proc_fees.waste_tire_cost?.checked ?? true,
        waste_tire_cost: formatNumber(mstProcFee.proc_fees.waste_tire_cost?.value) || "",
        rubber_stem_valve_flag: mstProcFee.proc_fees.rubber_stem_valve_cost?.checked ?? true,
        rubber_stem_valve_cost:
          formatNumber(mstProcFee.proc_fees.rubber_stem_valve_cost?.value) || "",
        nitrogen_inflation_flag: mstProcFee.proc_fees.nitrogen_inflation_cost?.checked ?? false,
        nitrogen_inflation_cost:
          formatNumber(mstProcFee.proc_fees.nitrogen_inflation_cost?.value) || "",
        hub_rust_prevention_flag: mstProcFee.proc_fees.hub_rust_prevention_cost?.checked ?? false,
        hub_rust_prevention_cost:
          formatNumber(mstProcFee.proc_fees.hub_rust_prevention_cost?.value) || "",
        tire_puncture_flag: mstProcFee.cashback?.puncture ?? 0,
        cashback_flag: (mstProcFee.cashback?.cashback_period ?? 1) > 0,
        cashback_period: mstProcFee.cashback?.cashback_period ?? 1,
      };
    }

    // Default values if no session data
    return {
      replac_labor_cost: "0",
      waste_tire_cost_flag: true,
      waste_tire_cost: "",
      rubber_stem_valve_flag: true,
      rubber_stem_valve_cost: "",
      nitrogen_inflation_flag: false,
      nitrogen_inflation_cost: "",
      hub_rust_prevention_flag: false,
      hub_rust_prevention_cost: "",
      tire_puncture_flag: 0,
      cashback_flag: false,
      cashback_period: 1,
    };
  });
  const [tireList, setTireList] = useState<ITireItemDetail[]>([]);
  const [selectedTireIds, setSelectedTireIds] = useState<Record<string, boolean>>({});
  const [originTireMakerList, setOriginTireMakerList] = useState<ITireItemDetail[]>([]);
  const [makerList, setMakerList] = useState<TOption[]>([]);
  const {
    selectedWheel,
    selectedCar,
    tireSize,
    storageTire,
    storageSelectedMaker,
    storageSelectedCategory,
    currentPage,
    newQuoteFrom,
    newQuoteWheel,
    newQuoteTire,
  } = useSessionData();
  const [selectedMaker, setSelectedMaker] = useState<boolean[]>([]);
  const [recommendFlag, setRecommendFlag] = useState(false);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState<number>(1);
  const totalPage = useMemo(() => Math.ceil(total / PER_PAGE_DEFAULT), [total]);
  const [totalSelectedTire, setTotalSelectedTire] = useState<number>(0);
  const [categoryList, setCategoryList] = useState<CategoryListItem[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<boolean[]>([]);
  // response.list.store.price_setting_wheel — drives cost/fetch and calculator branch.
  const [priceSettingWheel, setPriceSettingWheel] = useState<number>(0);

  const { queryParams, applyFilter } = useProcFeeFilter({
    makerList,
    categoryList,
    selectedMaker,
    selectedCategory,
    setSelectedMaker,
    setSelectedCategory,
  });

  const [punctureProtection, setPunctureProtection] = useState<PunctureProtection[]>();

  const loadMoreRef = useRef(false);

  // Check if we have session data at initialization (for priority logic)
  const hasInitialSessionData = useRef(false);
  const mstProcFee = useMemo(() => {
    const data = JSON.parse(sessionStorage.getItem(SESSION_KEY.MST_PROC_WHEEL_FEE) || "{}");
    hasInitialSessionData.current = !!data?.proc_fees;
    return data;
  }, []);

  // Keyed by String(tire.id) — unique PK, unlike item_cd which may duplicate.
  const formatTireList = (list: ITireItemDetail[], priceSetting: number) => {
    const formatedList = list
      .filter((tire) => tire.list_price !== null)
      .map((tire) => {
        const matchedTire = mstProcFee?.tbl_quote_detail_tire?.find(
          (item: Partial<EstQuoteDetailTire>) => item.tire_item_cd === tire.item_cd,
        );

        // Functional updater merges with existing selections — preserves page-1 picks
        // when new pages are appended (String(tire.id) keys are disjoint between pages).
        setSelectedTireIds((prev) => ({
          ...prev,
          [String(tire.id)]: !!matchedTire,
        }));

        if (matchedTire) {
          setTotalSelectedTire((prevTotal) => prevTotal + 1);
        }

        // Store-level price_setting_wheel attached per-item for the calculator.
        const withPriceSetting = { ...tire, price_setting_wheel: priceSetting };
        return matchedTire
          ? { ...withPriceSetting, special_discount: matchedTire.special_discount }
          : withPriceSetting;
      });

    return formatedList;
  };

  const getProcFees = async (body: MstItemDetailTireSelect) => {
    setLoading(true);
    try {
      const params = {
        ...storageTire[0],
        [storageTire.length > 1 ? "item_cds" : "item_cd"]:
          storageTire.length > 1
            ? storageTire.map((el) => el.item_cd).join(",")
            : storageTire[0].item_cd,
      };

      const prevScrollY = document.documentElement.scrollTop || document.body.scrollTop;
      if (storageTire.length > 1) delete params.item_cd;
      const response = (await getItemDetailTireSetApi({
        ...body,
        ...params,
      } as SelectedTireProcFeeParams)) as TireResponse;
      setTotal(response.total);

      // Only reset selectedTireIds when not load more (offset = 1)
      if (body && body?.offset === 1) {
        setSelectedTireIds({});
        setTotalSelectedTire(0);
      }

      // Response-level price setting flag from list.store.price_setting_wheel.
      const responsePriceSettingWheel = response.list?.store?.price_setting_wheel ?? 0;
      setPriceSettingWheel(responsePriceSettingWheel);

      // Format tire list (tempPage removed — selection is keyed by item_cd)
      const formattedList = formatTireList(
        response.list?.mst_item_detail_tire || [],
        responsePriceSettingWheel,
      );

      // Fetch inventory data and map to tire list
      const tireListWithInventory = await fetchAndMapInventory(
        response.list?.mst_item_detail_tire || [],
        formattedList,
      );

      if (body && body?.offset > 1) {
        setTireList((prev) => [...prev, ...tireListWithInventory]);
        setOriginTireMakerList((prev) => [...prev, ...(response.list?.mst_item_detail_tire ?? [])]);
      } else {
        setTireList(tireListWithInventory);
        setOriginTireMakerList(response.list?.mst_item_detail_tire);

        if (makerList.length === 0) {
          const makers = response.list.maker_list.map((item) => ({
            label: item.maker_name,
            value: item.maker_name,
            selected: true,
          }));
          const newMakers = uniqBy(makers, "value");
          setMakerList(newMakers);

          if (storageSelectedMaker.length > 0) {
            setMakerList(storageSelectedMaker);
            setSelectedMaker(storageSelectedMaker.map((item: TOption) => item.selected as never));
          } else {
            setSelectedMaker(newMakers.map(() => true));
          }
        }

        if (categoryList.length === 0) {
          setCategoryList(
            response.list?.category_list?.map((el: { category: string }) => ({
              category: el.category,
              selected: false,
            })) || [],
          );

          if (storageSelectedCategory.length > 0) {
            setCategoryList(storageSelectedCategory);
            setSelectedCategory(
              storageSelectedCategory.map((item: CategoryListItem) => item.selected as never),
            );
          } else {
            setSelectedCategory(response.list.category_list.map(() => false));
          }
        }
      }

      // Only update cost settings from API if no session data exists
      // This prevents overwriting values when back from confirm screen
      if (!hasInitialSessionData.current) {
        const mstProcFeeResponse = response.list.mst_proc_fee;
        setCostSettingInfo((prev) => ({
          ...prev,
          replac_labor_cost: mstProcFeeResponse.replac_labor_cost
            ? String(mstProcFeeResponse.replac_labor_cost)
            : prev.replac_labor_cost,
          waste_tire_cost: mstProcFeeResponse.waste_tire_cost
            ? String(mstProcFeeResponse.waste_tire_cost)
            : prev.waste_tire_cost,
          rubber_stem_valve_cost: mstProcFeeResponse.rubber_stem_valve_cost
            ? String(mstProcFeeResponse.rubber_stem_valve_cost)
            : prev.rubber_stem_valve_cost,
          nitrogen_inflation_cost: mstProcFeeResponse.nitrogen_inflation_cost
            ? String(mstProcFeeResponse.nitrogen_inflation_cost)
            : prev.nitrogen_inflation_cost,
          hub_rust_prevention_cost: mstProcFeeResponse.hub_rust_prevention_cost
            ? String(mstProcFeeResponse.hub_rust_prevention_cost)
            : prev.hub_rust_prevention_cost,
        }));
      }

      window.scrollTo(0, prevScrollY || 0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const init = async () => {
      const recommendFlagRaw = sessionStorage.getItem(SESSION_KEY.RECOMMEND_FLAG);
      const storageRecommendFlag = recommendFlagRaw ? JSON.parse(recommendFlagRaw) : false;
      if (storageTire.length > 0) {
        setRecommendFlag(storageRecommendFlag);
      }
    };

    const fetchProcFees = async () => {
      if (storageTire.length > 0) {
        const promises = [];
        const initialFilter = buildProcFeeFilterQueryParams(
          storageSelectedMaker.length > 0 ? storageSelectedMaker : makerList,
          storageSelectedCategory.length > 0 ? storageSelectedCategory : categoryList,
          {
            selectedMaker: storageSelectedMaker.map((el) => el.selected ?? true),
            selectedCategory: storageSelectedCategory.map((el) => el.selected ?? false),
          },
        );
        for (let i = 1; i <= Number(currentPage); i += 1) {
          promises.push(
            getProcFees({
              offset: i,
              limit: PER_PAGE_DEFAULT,
              ...initialFilter,
            }),
          );
        }
        await Promise.all(promises);

        // Skip updating cost settings if we already have session data from initialization
        // This preserves values when back from confirm screen
        if (hasInitialSessionData.current) {
          // Session data already loaded during initialization - don't overwrite
          return;
        }

        // Update cost settings only if no session data was available at initialization
        setCostSettingInfo((prev) => ({
          ...prev,
          replac_labor_cost:
            formatNumber(mstProcFee?.proc_fees?.replac_labor_cost?.value) ||
            prev.replac_labor_cost ||
            "0",
          waste_tire_cost:
            formatNumber(mstProcFee?.proc_fees?.waste_tire_cost?.value) ||
            prev.waste_tire_cost ||
            "0",
          rubber_stem_valve_cost:
            formatNumber(mstProcFee?.proc_fees?.rubber_stem_valve_cost?.value) ||
            prev.rubber_stem_valve_cost ||
            "0",
          nitrogen_inflation_cost:
            formatNumber(mstProcFee?.proc_fees?.nitrogen_inflation_cost?.value) ||
            prev.nitrogen_inflation_cost ||
            "0",
          hub_rust_prevention_cost:
            formatNumber(mstProcFee?.proc_fees?.hub_rust_prevention_cost?.value) ||
            prev.hub_rust_prevention_cost ||
            "0",
          waste_tire_cost_flag:
            mstProcFee?.proc_fees?.waste_tire_cost?.checked ?? prev.waste_tire_cost_flag ?? true,
          rubber_stem_valve_flag:
            mstProcFee?.proc_fees?.rubber_stem_valve_cost?.checked ??
            prev.rubber_stem_valve_flag ??
            true,
          nitrogen_inflation_flag:
            mstProcFee?.proc_fees?.nitrogen_inflation_cost?.checked ??
            prev.nitrogen_inflation_flag ??
            false,
          hub_rust_prevention_flag:
            mstProcFee?.proc_fees?.hub_rust_prevention_cost?.checked ??
            prev.hub_rust_prevention_flag ??
            false,
          tire_puncture_flag: mstProcFee?.cashback?.puncture ?? prev.tire_puncture_flag ?? 0,
          cashback_period: mstProcFee?.cashback?.cashback_period ?? prev.cashback_period ?? 1,
          cashback_flag: (mstProcFee?.cashback?.cashback_period ?? prev.cashback_period ?? 1) > 0,
        }));
      }
    };

    const setDataFromCarWheelProductCode = () => {
      setTireList(newQuoteTire);
    };
    init();
    if (newQuoteFrom !== routes.mstCarWheelProductCode) {
      fetchProcFees();
    } else {
      setDataFromCarWheelProductCode();
    }
  }, [storageTire.length]);

  useEffect(() => {
    const fetchPunctureProtection = async () => {
      const response = await getPructureProtectionApi();
      setPunctureProtection(
        response.list.sort(
          (a: { coverage_amount_low: number }, b: { coverage_amount_low: number }) =>
            a.coverage_amount_low - b.coverage_amount_low,
        ),
      );
    };
    fetchPunctureProtection();
  }, []);

  const handleFilter = async (overrides?: ProcFeeFilterSelection) => {
    const resolved = applyFilter(overrides);
    setTotal(0);
    setPage(1);
    setOriginTireMakerList([]);
    loadMoreRef.current = false;

    const filterParams = buildProcFeeFilterQueryParams(makerList, categoryList, resolved);

    await getProcFees({
      offset: 1,
      limit: PER_PAGE_DEFAULT,
      ...filterParams,
    });

    sessionStorage.setItem(
      SESSION_KEY.WHEEL_SELECTED_MAKER,
      JSON.stringify(
        makerList.map((el, index) => ({
          ...el,
          selected: resolved.selectedMaker[index],
        })),
      ),
    );
    sessionStorage.setItem(
      SESSION_KEY.WHEEL_SELECTED_CATEGORY,
      JSON.stringify(
        categoryList.map((el, index) => ({
          ...el,
          selected: resolved.selectedCategory[index],
        })),
      ),
    );
  };

  const onLoadMore = useCallback(async () => {
    if (
      page + 1 > totalPage ||
      originTireMakerList.length === 0 ||
      originTireMakerList.length < PER_PAGE_DEFAULT ||
      loadMoreRef.current
    )
      return;

    // Set flag to prevent multiple calls
    loadMoreRef.current = true;

    setPage((prevPage) => prevPage + 1);
    const newParams: MstItemDetailTireSelect = {
      offset: page + 1,
      limit: PER_PAGE_DEFAULT,
      ...queryParams,
    };

    try {
      await getProcFees(newParams);
    } finally {
      // Reset flag after API call completes
      loadMoreRef.current = false;
    }
  }, [page, totalPage, originTireMakerList.length, queryParams]);

  // Optimized scroll handler with throttling
  useEffect(() => {
    let scrollTimeout: NodeJS.Timeout | null = null;

    const handleScroll = () => {
      // Clear previous timeout
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }

      // Throttle scroll events
      scrollTimeout = setTimeout(() => {
        const { scrollHeight } = document.documentElement;
        const scrollTop = window.innerHeight + window.scrollY;
        const threshold = scrollHeight - 100; // 100px before bottom

        if (
          scrollTop >= threshold &&
          total > originTireMakerList.length &&
          !loading &&
          !loadMoreRef.current
        ) {
          onLoadMore();
        }
      }, 200); // Increased throttle to 200ms for better protection
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
    };
  }, [total, originTireMakerList.length, loading, onLoadMore]);

  return {
    costSettingInfo,
    setCostSettingInfo,
    makerList,
    setMakerList,
    handleFilter,
    onLoadMore,
    loading,
    selectedMaker,
    setSelectedMaker,
    recommendFlag,
    setRecommendFlag,
    selectedWheel,
    selectedCar,
    tireSize,
    tireList,
    setTireList,
    punctureProtection,
    mstProcFee,
    selectedTireIds,
    setTotalSelectedTire,
    totalSelectedTire,
    setSelectedTireIds,
    categoryList,
    selectedCategory,
    setSelectedCategory,
    page,
    priceSettingWheel,
  };
};

export default useWheelTireProcFeeList;
